import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Calendar, Clock, ChevronLeft, ChevronRight, Plus, Trash2, 
  LogIn, LogOut, AlertTriangle, CheckCircle, Timer, Settings, X, UserPlus
} from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const DAYS_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function getWeekDates(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const monday = new Date(now);
  monday.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d.toISOString().split('T')[0];
  });
}

function formatDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
}

function getStatusColor(status) {
  switch (status) {
    case 'scheduled': return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'clocked_in': return 'bg-green-100 text-green-700 border-green-200';
    case 'completed': return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'missed': return 'bg-red-100 text-red-700 border-red-200';
    default: return 'bg-gray-100 text-gray-600';
  }
}

function getStatusBadge(status) {
  switch (status) {
    case 'scheduled': return 'secondary';
    case 'clocked_in': return 'default';
    case 'completed': return 'outline';
    case 'missed': return 'destructive';
    default: return 'outline';
  }
}

export default function StaffSchedule() {
  const { user } = useAuth();
  const [weekOffset, setWeekOffset] = useState(0);
  const [schedules, setSchedules] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // {staffId, date}
  const [settings, setSettings] = useState({ enabled: true, late_threshold_minutes: 15, alert_emails: [] });
  const [newAlertEmail, setNewAlertEmail] = useState('');
  const [createForm, setCreateForm] = useState({ start_time: '09:00', end_time: '17:00', notes: '' });
  const [myShifts, setMyShifts] = useState([]); // current user's recent shifts (yesterday..today) for the personal panel

  const weekDates = getWeekDates(weekOffset);
  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [schedRes, staffRes, settingsRes] = await Promise.all([
        api.get(`/schedules?start_date=${weekStart}&end_date=${weekEnd}`),
        api.get('/staff'),
        api.get('/schedules/settings'),
      ]);
      setSchedules(schedRes.data);
      setStaff(staffRes.data);
      setSettings(settingsRes.data);
      // Fetch the current user's own recent shifts (yesterday..today) so an open
      // overnight shift is surfaced even when it falls outside the viewed week.
      const myMember = (staffRes.data || []).find(s => s.email === user?.email);
      if (myMember) {
        const today = new Date();
        const yesterday = new Date(today.getTime() - 86400000);
        const d = (dt) => dt.toISOString().split('T')[0];
        try {
          const mineRes = await api.get(`/schedules?staff_id=${myMember.id}&start_date=${d(yesterday)}&end_date=${d(today)}`);
          setMyShifts(mineRes.data || []);
        } catch { setMyShifts([]); }
      } else {
        setMyShifts([]);
      }
    } catch (e) {
      console.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd, user?.email]);

  useEffect(() => { loadData(); }, [loadData]);

  const getScheduleForCell = (staffId, date) => {
    return schedules.find(s => s.staff_id === staffId && s.date === date);
  };

  const handleCreateSchedule = async () => {
    if (!selectedCell) return;
    const staffMember = staff.find(s => s.id === selectedCell.staffId);
    if (!staffMember) return;
    try {
      await api.post('/schedules', {
        staff_id: staffMember.id,
        staff_name: staffMember.full_name,
        staff_email: staffMember.email,
        date: selectedCell.date,
        ...createForm,
      });
      toast.success('Schedule created');
      setShowCreateDialog(false);
      setCreateForm({ start_time: '09:00', end_time: '17:00', notes: '' });
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create schedule');
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success('Schedule removed');
      loadData();
    } catch (e) {
      toast.error('Failed to delete schedule');
    }
  };

  const handleClockIn = async (scheduleId) => {
    try {
      await api.post(`/schedules/${scheduleId}/clock-in`);
      toast.success('Clocked in successfully');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock in');
    }
  };

  const handleClockOut = async (scheduleId) => {
    try {
      await api.post(`/schedules/${scheduleId}/clock-out`);
      toast.success('Clocked out successfully');
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock out');
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/schedules/settings', settings);
      toast.success('Settings saved');
      setShowSettingsDialog(false);
    } catch (e) {
      toast.error('Failed to save settings');
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  // --- Personal "My Shift" panel data (req5: own clock in/out) ---
  const myMember = staff.find(s => s.email === user?.email);
  const myRequireClockIn = myMember?.require_clock_in !== false;
  const myOpenOvernight = myShifts.find(s => s.status === 'clocked_in' && s.date < todayStr);
  const myTodayShift = myShifts.find(s => s.date === todayStr)
    || schedules.find(s => myMember && s.staff_id === myMember.id && s.date === todayStr);

  return (
    <div className="p-4 md:p-6 max-w-full" data-testid="staff-schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Staff Schedule</h1>
          <p className="text-sm text-gray-500 mt-1">Manage schedules and clock in/out</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setShowSettingsDialog(true)} data-testid="schedule-settings-btn">
            <Settings className="h-4 w-4 mr-1" /> Settings
          </Button>
        </div>
      </div>

      {/* My Shift panel (req5: own clock in/out) */}
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="my-shift-panel">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="h-4 w-4 text-blue-700" />
          <span className="font-semibold text-sm text-blue-900">My shift</span>
        </div>
        {myOpenOvernight ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="my-shift-overnight">
            <div className="text-sm text-amber-800">
              You're still clocked in from <strong>{formatDate(myOpenOvernight.date)}</strong> ({myOpenOvernight.start_time}–{myOpenOvernight.end_time}). Don't forget to clock out.
            </div>
            <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
              onClick={() => handleClockOut(myOpenOvernight.id)} data-testid="my-shift-clock-out-overnight">
              Clock Out
            </Button>
          </div>
        ) : myTodayShift ? (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2" data-testid="my-shift-today">
            <div className="text-sm text-gray-700">
              Today's shift: <strong>{myTodayShift.start_time}–{myTodayShift.end_time}</strong>
              {myTodayShift.status === 'clocked_in' && <span className="ml-2 text-green-700">• Clocked in</span>}
              {myTodayShift.status === 'completed' && <span className="ml-2 text-gray-500">• Completed</span>}
            </div>
            {myRequireClockIn && myTodayShift.status === 'scheduled' && (
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                onClick={() => handleClockIn(myTodayShift.id)} data-testid="my-shift-clock-in">
                Clock In
              </Button>
            )}
            {myRequireClockIn && myTodayShift.status === 'clocked_in' && (
              <Button size="sm" variant="outline" className="text-red-700 border-red-300 hover:bg-red-50"
                onClick={() => handleClockOut(myTodayShift.id)} data-testid="my-shift-clock-out">
                Clock Out
              </Button>
            )}
          </div>
        ) : (
          <div className="text-sm text-gray-500" data-testid="my-shift-none">
            You have no shift scheduled today.
          </div>
        )}
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between mb-4 bg-white rounded-lg border p-3">
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w - 1)} data-testid="prev-week-btn">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="text-center">
          <span className="font-semibold text-sm md:text-base">
            {formatDate(weekStart)} — {formatDate(weekEnd)}
          </span>
          {weekOffset !== 0 && (
            <Button variant="link" size="sm" className="ml-2 text-xs" onClick={() => setWeekOffset(0)}>
              Today
            </Button>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setWeekOffset(w => w + 1)} data-testid="next-week-btn">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading schedules...</div>
      ) : (
        <>
          {/* Desktop: Weekly Grid */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg border" data-testid="schedule-grid">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border-b border-r px-3 py-2 text-left text-sm font-semibold w-40">Staff</th>
                  {weekDates.map((date, i) => (
                    <th key={date} className={`border-b px-2 py-2 text-center text-sm font-semibold ${date === todayStr ? 'bg-blue-50' : ''}`}>
                      <div>{DAYS[i]}</div>
                      <div className="text-xs font-normal text-gray-500">{formatDate(date)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id} className="border-b last:border-b-0">
                    <td className="border-r px-3 py-2 text-sm font-medium whitespace-nowrap">{member.full_name}</td>
                    {weekDates.map((date) => {
                      const sched = getScheduleForCell(member.id, date);
                      return (
                        <td
                          key={date}
                          className={`px-1 py-1 text-center align-top min-w-[120px] ${date === todayStr ? 'bg-blue-50/50' : ''}`}
                        >
                          {sched ? (
                            <ScheduleCell
                              schedule={sched}
                              isCurrentUser={member.email === user?.email}
                              requireClockIn={member.require_clock_in !== false}
                              onClockIn={() => handleClockIn(sched.id)}
                              onClockOut={() => handleClockOut(sched.id)}
                              onDelete={() => handleDeleteSchedule(sched.id)}
                              isToday={date === todayStr}
                            />
                          ) : (
                            <button
                              onClick={() => { setSelectedCell({ staffId: member.id, date }); setShowCreateDialog(true); }}
                              className="w-full h-14 rounded border border-dashed border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-colors flex items-center justify-center text-gray-400 hover:text-blue-500"
                              data-testid={`add-schedule-${member.id}-${date}`}
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: Stacked Day View */}
          <div className="md:hidden space-y-4" data-testid="schedule-mobile">
            {weekDates.map((date, i) => {
              const daySchedules = schedules.filter(s => s.date === date);
              return (
                <Card key={date} className={date === todayStr ? 'ring-2 ring-blue-400' : ''}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm">
                        {DAYS_FULL[i]} <span className="text-gray-500 font-normal">{formatDate(date)}</span>
                      </h3>
                      {date === todayStr && <Badge variant="default" className="text-xs">Today</Badge>}
                    </div>
                    {daySchedules.length === 0 ? (
                      <p className="text-xs text-gray-400 py-2">No schedules</p>
                    ) : (
                      <div className="space-y-2">
                        {daySchedules.map(sched => {
                          const member = staff.find(s => s.id === sched.staff_id);
                          const isCurrentUser = member?.email === user?.email;
                          const requireClockIn = member?.require_clock_in !== false;
                          return (
                            <div key={sched.id} className={`flex items-center justify-between p-2 rounded border ${getStatusColor(sched.status)}`}>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium truncate">{sched.staff_name}</div>
                                <div className="text-xs flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTime12(sched.start_time)} - {formatTime12(sched.end_time)}
                                </div>
                                {sched.is_late && <Badge variant="destructive" className="text-xs mt-1">Late</Badge>}
                                {sched.overtime_minutes > 0 && <Badge variant="outline" className="text-xs mt-1 border-amber-400 text-amber-600">OT +{sched.overtime_minutes}m</Badge>}
                              </div>
                              <div className="flex items-center gap-1 shrink-0 ml-2">
                                {sched.status === 'scheduled' && isCurrentUser && requireClockIn && date === todayStr && (
                                  <Button size="sm" variant="default" onClick={() => handleClockIn(sched.id)} data-testid={`mobile-clock-in-${sched.id}`}>
                                    <LogIn className="h-3 w-3 mr-1" /> In
                                  </Button>
                                )}
                                {sched.status === 'clocked_in' && isCurrentUser && requireClockIn && (
                                  <Button size="sm" variant="outline" onClick={() => handleClockOut(sched.id)} data-testid={`mobile-clock-out-${sched.id}`}>
                                    <LogOut className="h-3 w-3 mr-1" /> Out
                                  </Button>
                                )}
                                {sched.status === 'completed' && <CheckCircle className="h-4 w-4 text-green-500" />}
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteSchedule(sched.id)}>
                                  <Trash2 className="h-3 w-3 text-red-400" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {/* Mobile add button */}
                    <MobileAddSchedule staff={staff} date={date} onCreated={loadData} />
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Create Schedule Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Schedule</DialogTitle>
          </DialogHeader>
          {selectedCell && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Staff: <span className="font-medium text-gray-900">{staff.find(s => s.id === selectedCell.staffId)?.full_name}</span></p>
                <p className="text-sm text-gray-500">Date: <span className="font-medium text-gray-900">{selectedCell.date}</span></p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Start Time</label>
                  <Input type="time" value={createForm.start_time} onChange={e => setCreateForm(f => ({...f, start_time: e.target.value}))} data-testid="schedule-start-time" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">End Time</label>
                  <Input type="time" value={createForm.end_time} onChange={e => setCreateForm(f => ({...f, end_time: e.target.value}))} data-testid="schedule-end-time" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Notes (optional)</label>
                <Input value={createForm.notes} onChange={e => setCreateForm(f => ({...f, notes: e.target.value}))} placeholder="e.g. Morning shift" data-testid="schedule-notes" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateSchedule} data-testid="save-schedule-btn">Save Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Alert Settings</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Late Threshold (minutes)</label>
              <Input
                type="number"
                min={1}
                value={settings.late_threshold_minutes}
                onChange={e => setSettings(s => ({...s, late_threshold_minutes: parseInt(e.target.value) || 15}))}
                data-testid="late-threshold-input"
              />
              <p className="text-xs text-gray-500 mt-1">Staff are flagged as late after this many minutes past their scheduled start</p>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Supervisor/Manager Alert Emails</label>
              {settings.alert_emails?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {settings.alert_emails.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-xs">
                      {email}
                      <button onClick={() => setSettings(s => ({...s, alert_emails: s.alert_emails.filter((_, i) => i !== idx)}))} className="text-gray-400 hover:text-red-500">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="manager@company.com"
                  value={newAlertEmail}
                  onChange={e => setNewAlertEmail(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newAlertEmail.trim()) {
                      setSettings(s => ({...s, alert_emails: [...(s.alert_emails || []), newAlertEmail.trim()]}));
                      setNewAlertEmail('');
                    }
                  }}
                  data-testid="alert-email-input"
                />
                <Button size="sm" variant="outline" onClick={() => {
                  if (newAlertEmail.trim()) {
                    setSettings(s => ({...s, alert_emails: [...(s.alert_emails || []), newAlertEmail.trim()]}));
                    setNewAlertEmail('');
                  }
                }}>Add</Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">These people will be alerted when staff haven't clocked in on time</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} data-testid="save-settings-btn">Save Settings</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Schedule cell in desktop grid */
function ScheduleCell({ schedule, isCurrentUser, requireClockIn, onClockIn, onClockOut, onDelete, isToday }) {
  return (
    <div className={`rounded border p-1.5 text-xs relative group ${getStatusColor(schedule.status)}`} data-testid={`schedule-cell-${schedule.id}`}>
      <div className="font-medium">
        {formatTime12(schedule.start_time)} - {formatTime12(schedule.end_time)}
      </div>
      {schedule.notes && <div className="text-[10px] text-gray-500 truncate">{schedule.notes}</div>}
      <div className="flex items-center gap-1 mt-1 justify-center flex-wrap">
        <Badge variant={getStatusBadge(schedule.status)} className="text-[10px] px-1.5 py-0">
          {schedule.status === 'clocked_in' ? 'In' : schedule.status === 'completed' ? 'Done' : schedule.status}
        </Badge>
        {schedule.is_late && (
          <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />Late
          </Badge>
        )}
        {schedule.overtime_minutes > 0 && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-400 text-amber-600">
            <Timer className="h-2.5 w-2.5 mr-0.5" />+{schedule.overtime_minutes}m
          </Badge>
        )}
      </div>
      {/* Action buttons */}
      <div className="flex items-center gap-0.5 mt-1 justify-center">
        {schedule.status === 'scheduled' && isCurrentUser && requireClockIn && isToday && (
          <Button size="sm" variant="default" className="h-6 text-[10px] px-2" onClick={onClockIn} data-testid={`clock-in-${schedule.id}`}>
            <LogIn className="h-3 w-3 mr-0.5" /> Clock In
          </Button>
        )}
        {schedule.status === 'clocked_in' && isCurrentUser && requireClockIn && (
          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={onClockOut} data-testid={`clock-out-${schedule.id}`}>
            <LogOut className="h-3 w-3 mr-0.5" /> Clock Out
          </Button>
        )}
        <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-400 hover:text-red-600 p-0.5" data-testid={`delete-schedule-${schedule.id}`}>
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/* Mobile add schedule inline */
function MobileAddSchedule({ staff, date, onCreated }) {
  const [open, setOpen] = useState(false);
  const [staffId, setStaffId] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [notes, setNotes] = useState('');

  const handleAdd = async () => {
    const member = staff.find(s => s.id === staffId);
    if (!member) { toast.error('Select a staff member'); return; }
    try {
      await api.post('/schedules', {
        staff_id: member.id,
        staff_name: member.full_name,
        staff_email: member.email,
        date, start_time: startTime, end_time: endTime, notes
      });
      toast.success('Schedule created');
      setOpen(false);
      setStaffId('');
      setNotes('');
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="mt-2 w-full py-1.5 rounded border border-dashed border-gray-300 text-xs text-gray-400 hover:text-blue-500 hover:border-blue-400 transition-colors flex items-center justify-center gap-1"
        data-testid={`mobile-add-schedule-${date}`}
      >
        <Plus className="h-3 w-3" /> Add Schedule
      </button>
    );
  }

  return (
    <div className="mt-2 p-2 rounded border bg-gray-50 space-y-2">
      <Select value={staffId} onValueChange={setStaffId}>
        <SelectTrigger className="text-sm" data-testid="mobile-staff-select">
          <SelectValue placeholder="Select staff" />
        </SelectTrigger>
        <SelectContent>
          {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="grid grid-cols-2 gap-2">
        <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} />
        <Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} />
      </div>
      <Input placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
      <div className="flex gap-2">
        <Button size="sm" className="flex-1" onClick={handleAdd}><UserPlus className="h-3 w-3 mr-1" /> Add</Button>
        <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}
